import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import EmojiPicker, { Emoji, EmojiStyle, Theme } from "emoji-picker-react";
import "./themeupdate.css";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { fetchThemeData } from "../../themeSlice";
import { getDvSourceImageUrl } from "../../../functions/themeAssets";
import { updateThemeData } from "../../functions/updateThemeData";
import { TOUR_STEPS } from "../../tour/tourSteps";

const MAX_GOOD = 10;
const MAX_BAD  = 10;
const MIN_GOOD = 2;
const MIN_BAD  = 2;
const EMOJI_W  = 280;
const EMOJI_H  = 380;
const MAX_FEEDBACK_MSG_LEN = 100;
const MAX_TIER_NAME_LEN = 20;
const MAX_ITEM_NAME_LEN = 15;
const MAX_DESC_LEN = 150;

const DEFAULT_GOOD_NAMES = [
  "Dumbbells", "Yoga Mat", "Bicycle", "Vegetables",
  "Fruits", "Salad", "Water Bottle",
];
const DEFAULT_BAD_NAMES = [
  "French Fries", "Burger", "Soda", "Donut",
  "Pizza", "Cigarette", "Alcohol", "Cake",
];

const DEFAULT_FEEDBACK = [
  { scoreLabel: "≤ 0",     scoreKey: "le_0",    emoji: "🥀", unified: "1f940", message: "Heart is struggling. Choose healthier items" },
  { scoreLabel: "5 – 20",  scoreKey: "5_20",    emoji: "😟", unified: "1f61f", message: "Needs care. Keep improving choices" },
  { scoreLabel: "25 – 40", scoreKey: "25_40",   emoji: "🙂", unified: "1f642", message: "Getting there. Heart improving" },
  { scoreLabel: "45 – 60", scoreKey: "45_60",   emoji: "😃", unified: "1f603", message: "Heart is doing well. Great choices" },
  { scoreLabel: "65+",     scoreKey: "65_plus", emoji: "🏆", unified: "1f3c6", message: "Heart is thriving! Excellent job" },
];

const DEFAULT_LEADERBOARD = [
  { scoreLabel: "65+",     scoreKey: "65_plus", tierName: "Heart Hero" },
  { scoreLabel: "45 – 60", scoreKey: "45_60",   tierName: "Pulse Pro" },
  { scoreLabel: "25 – 40", scoreKey: "25_40",   tierName: "Wellness Rookie" },
  { scoreLabel: "5 – 20",  scoreKey: "5_20",    tierName: "Needs Attention" },
  { scoreLabel: "≤ 0",     scoreKey: "le_0",    tierName: "At Risk" },
];

function blankItem(name = "", isGood = true) {
  return {
    name,
    type: "text",
    description: "",
    contentFile: null, contentFilename: "", contentPreview: "",
    mascotType: "gif", mascotText: "",
    mascotFile: null,  mascotFilename: "",  mascotPreview: "",
    isGood,
  };
}

function ensureArr(val) {
  if (Array.isArray(val)) return val;
  if (!val) return [];
  try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; }
}

function pickPos(el) {
  const r = el.getBoundingClientRect(), pad = 8;
  let left = r.left, top = r.bottom + 8;
  if (left + EMOJI_W > window.innerWidth - pad) left = Math.max(pad, window.innerWidth - EMOJI_W - pad);
  if (left < pad) left = pad;
  if (top + EMOJI_H > window.innerHeight - pad) top = Math.max(pad, r.top - EMOJI_H - 8);
  if (top < pad) top = pad;
  return { top, left };
}

function parseStoredBool(raw, fallback = false) {
  if (raw === undefined || raw === null || raw === "") return fallback;
  if (typeof raw === "boolean") return raw;
  const s = String(raw).trim().toLowerCase();
  if (s === "true" || s === "1" || s === "yes") return true;
  if (s === "false" || s === "0" || s === "no") return false;
  return fallback;
}

function ItemNameToggle({ checked, onChange, disabled, label = "Item name" }) {
  return (
    <div className="hs-item-name-toggle">
      <span className="hs-item-name-toggle-label">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={`${label} ${checked ? "on" : "off"}`}
        className={`hs-switch${checked ? " hs-switch--on" : " hs-switch--off"}`}
        onClick={() => onChange(!checked)}
        disabled={disabled}
      >
        <span className="hs-switch-knob" />
      </button>
    </div>
  );
}

async function uploadImageFile(file, adminToken, sessionId) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("sessionId", sessionId || "admin");
  const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/uploadHeartAssets`, {
    method: "POST",
    headers: { Authorization: `Bearer ${adminToken}` },
    body: fd,
  });
  const body = await resp.json().catch(() => ({}));
  if (!resp.ok || !body.success) throw new Error(body.message || "Image upload failed");
  return body.files?.file || "";
}

const MAX_FILE_BYTES = 2 * 1024 * 1024; // 2 MB per image
const MAX_MASCOT_TEXT_LEN = 40;

/** Any pixel dimensions are accepted (the game fits them into their slot); only file weight is capped. */
function isFileSizeOk(file) {
  if (file.size <= MAX_FILE_BYTES) return true;
  Swal.fire("File Too Large", `File must be ${MAX_FILE_BYTES / (1024 * 1024)} MB or smaller.`, "warning");
  return false;
}

// ─────────────────── ItemCard ─────────────────── (defined outside to respect hooks rules)
function ItemCard({ item, idx, isGood, onUpdate, onRemove, disabled, showDescription }) {
  const contentRef = useRef(null);
  const mascotRef  = useRef(null);
  const contentSrc = item.contentPreview || (item.contentFilename ? getDvSourceImageUrl(item.contentFilename) : "");
  const mascotSrc  = item.mascotPreview  || (item.mascotFilename  ? getDvSourceImageUrl(item.mascotFilename)  : "");

  async function openDescriptionPopup() {
    const { isConfirmed, value } = await Swal.fire({
      title: `Description${item.name ? ` – ${item.name}` : ""}`,
      input: "textarea",
      inputValue: item.description || "",
      inputPlaceholder: "Shown in the game as a \"Did you Know?\" popup",
      inputAttributes: { maxlength: String(MAX_DESC_LEN) },
      inputValidator: (v) => (v.length > MAX_DESC_LEN ? `Max ${MAX_DESC_LEN} characters.` : undefined),
      footer: `Max ${MAX_DESC_LEN} characters`,
      showCancelButton: true,
      confirmButtonText: "Save",
    });
    if (isConfirmed) onUpdate(idx, { description: String(value ?? "").trim().slice(0, MAX_DESC_LEN) });
  }

  async function handleContentFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const name = file.name.toLowerCase();
    if (item.type === "gif") {
      if (!name.endsWith(".gif")) {
        Swal.fire("Invalid File", "Item GIF must be a GIF file.", "warning");
        e.target.value = "";
        return;
      }
    } else if (item.type === "image") {
      if (!name.endsWith(".png")) {
        Swal.fire("Invalid File", "Item image must be a PNG file.", "warning");
        e.target.value = "";
        return;
      }
    }
    if (!isFileSizeOk(file)) {
      e.target.value = "";
      return;
    }
    onUpdate(idx, { contentFile: file, contentPreview: URL.createObjectURL(file), contentFilename: "" });
  }

  async function handleMascotFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (item.mascotType === "gif" && !lower.endsWith(".gif")) {
      Swal.fire("Invalid File", "Mascot GIF must be a GIF file.", "warning");
      e.target.value = "";
      return;
    }
    if (item.mascotType === "image" && !lower.endsWith(".png")) {
      Swal.fire("Invalid File", "Mascot image must be a PNG file.", "warning");
      e.target.value = "";
      return;
    }
    if (!isFileSizeOk(file)) {
      e.target.value = "";
      return;
    }
    onUpdate(idx, { mascotFile: file, mascotPreview: URL.createObjectURL(file), mascotFilename: "" });
  }

  return (
    <div className={`hs-item-card${isGood ? " hs-item-card--good" : " hs-item-card--bad"}`}>
      <div className="hs-item-head">
        <span className={`hs-item-badge${isGood ? " hs-badge-good" : " hs-badge-bad"}`}>
          {isGood ? <i className="fa-solid fa-heart" /> : <i className="fa-solid fa-heart-crack" />}
          {" "}{idx + 1}
        </span>
        <button
          type="button"
          className="hs-item-remove"
          onClick={() => onRemove(idx)}
          disabled={disabled}
          title="Remove item"
        >
          <i className="fa-solid fa-xmark" />
        </button>
      </div>

      <div className="hs-item-body">
        <label className="hs-field-label">
          Item Name <span className="hs-char-count">{item.name.length}/{MAX_ITEM_NAME_LEN}</span>
        </label>
        <input
          type="text"
          className="themeupdate-input"
          placeholder="e.g. Dumbbells"
          value={item.name}
          maxLength={MAX_ITEM_NAME_LEN}
          onChange={e => {
            const v = e.target.value;
            if (v.length > MAX_ITEM_NAME_LEN) {
              Swal.fire("Limit", `Item name max ${MAX_ITEM_NAME_LEN} characters.`, "warning");
              return;
            }
            onUpdate(idx, { name: v });
          }}
        />

        {showDescription && (
          <>
            <label className="hs-field-label">
              Description <span className="hs-char-count">{(item.description || "").length}/{MAX_DESC_LEN}</span>
            </label>
            <button
              type="button"
              className="hs-type-btn"
              onClick={openDescriptionPopup}
              disabled={disabled}
            >
              <i className="fa-solid fa-pen" /> {item.description ? "Edit Description" : "Add Description"}
            </button>
            {item.description && <p className="hs-desc-preview">{item.description}</p>}
          </>
        )}

        <label className="hs-field-label">Display As</label>
        <div className="hs-type-row">
          {["text", "image", "gif"].map(t => (
            <button
              key={t}
              type="button"
              className={`hs-type-btn${item.type === t ? " hs-type-btn--active" : ""}`}
              onClick={() => onUpdate(idx, { type: t })}
            >
              {t === "text"  ? <><i className="fa-solid fa-font"  /> Text</>  :
               t === "image" ? <><i className="fa-solid fa-image" /> Image</> :
                               <><i className="fa-solid fa-film"  /> GIF</>}
            </button>
          ))}
        </div>

        {(item.type === "image" || item.type === "gif") && (
          <div className="hs-upload-block">
            <label className="hs-field-label">
              Item {item.type === "gif" ? "GIF" : "Image"}
            </label>
            <div className="hs-upload-area" onClick={() => contentRef.current?.click()}>
              {contentSrc ? (
                <img src={contentSrc} alt="item" className="hs-upload-thumb" />
              ) : (
                <div className="hs-upload-empty">
                  <i className={`fa-solid ${item.type === "gif" ? "fa-film" : "fa-image"}`} />
                  <span>Upload {item.type === "gif" ? "GIF" : "Image"}</span>
                </div>
              )}
            </div>
            <input
              ref={contentRef}
              type="file"
              accept={item.type === "gif" ? ".gif" : ".png"}
              className="hs-file-hidden"
              onChange={handleContentFile}
            />
            <span className="hs-upload-hint">
              {item.type === "gif" ? ".gif only" : ".png only"}, any size, max 2 MB
            </span>
          </div>
        )}

        <label className="hs-field-label">
          Mascot <span className="hs-badge-pill">Heart acts it out</span>
        </label>
        <div className="hs-type-row">
          {["text", "image", "gif"].map(t => (
            <button
              key={t}
              type="button"
              className={`hs-type-btn${item.mascotType === t ? " hs-type-btn--active" : ""}`}
              onClick={() => onUpdate(idx, { mascotType: t })}
            >
              {t === "text"  ? <><i className="fa-solid fa-font"  /> Text</>  :
               t === "image" ? <><i className="fa-solid fa-image" /> Image</> :
                               <><i className="fa-solid fa-film"  /> GIF</>}
            </button>
          ))}
        </div>

        {item.mascotType === "text" ? (
          <div className="hs-upload-block">
            <label className="hs-field-label">
              Mascot Text <span className="hs-char-count">{(item.mascotText || "").length}/{MAX_MASCOT_TEXT_LEN}</span>
            </label>
            <input
              type="text"
              className="themeupdate-input"
              placeholder="e.g. Great choice!"
              value={item.mascotText || ""}
              maxLength={MAX_MASCOT_TEXT_LEN}
              onChange={e => onUpdate(idx, { mascotText: e.target.value.slice(0, MAX_MASCOT_TEXT_LEN) })}
            />
          </div>
        ) : (
          <div className="hs-upload-block">
            <div className="hs-upload-area hs-upload-area--mascot" onClick={() => mascotRef.current?.click()}>
              {mascotSrc ? (
                <img src={mascotSrc} className="hs-upload-thumb" alt="mascot" />
              ) : item.mascotFilename ? (
                <div className="hs-upload-empty hs-upload-has-file">
                  <i className="fa-solid fa-circle-check" />
                  <span className="hs-filename-short">
                    {item.mascotFilename.split("-").pop() || item.mascotFilename}
                  </span>
                </div>
              ) : (
                <div className="hs-upload-empty">
                  <i className={`fa-solid ${item.mascotType === "gif" ? "fa-film" : "fa-image"}`} />
                  <span>Upload {item.mascotType === "gif" ? "GIF" : "Image"}</span>
                </div>
              )}
            </div>
            <input
              ref={mascotRef}
              type="file"
              accept={item.mascotType === "gif" ? ".gif" : ".png"}
              className="hs-file-hidden"
              onChange={handleMascotFile}
            />
            <span className="hs-upload-hint">
              {item.mascotType === "gif" ? ".gif only" : ".png only"}, any size, max 2 MB
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────── IdleCard ─────────────────── (GIF only, 512x512)
function IdleCard({ label, description, state, onFile }) {
  const ref = useRef(null);
  const src = state.preview || (state.filename ? getDvSourceImageUrl(state.filename) : "");

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".gif")) {
      Swal.fire("Invalid File", "Please upload a GIF file.", "warning");
      e.target.value = "";
      return;
    }
    if (!isFileSizeOk(file)) {
      e.target.value = "";
      return;
    }
    onFile({ file, preview: URL.createObjectURL(file), filename: file.name });
  }

  return (
    <div className="hs-idle-card">
      <div className="hs-idle-head">{label}</div>
      {description && <div className="hs-idle-desc">{description}</div>}
      <div className="hs-upload-area hs-upload-area--lg" onClick={() => ref.current?.click()}>
        {src ? (
          <img src={src} className="hs-upload-thumb-lg" alt="idle mascot" />
        ) : state.filename ? (
          <div className="hs-upload-empty hs-upload-has-file">
            <i className="fa-solid fa-circle-check" />
            <span className="hs-filename-short">
              {state.filename.split("-").pop() || state.filename}
            </span>
          </div>
        ) : (
          <div className="hs-upload-empty">
            <i className="fa-solid fa-heart-pulse" />
            <span>Upload GIF</span>
          </div>
        )}
      </div>
      <input ref={ref} type="file" accept=".gif" className="hs-file-hidden" onChange={handleFile} />
      <span className="hs-upload-hint">.gif only, any size, max 2 MB</span>
    </div>
  );
}

// ─────────────────── Main Component ───────────────────
const ThemeUpdate = () => {
  const dispatch       = useDispatch();
  const navigate       = useNavigate();
  const adminToken     = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId      = useSelector(selectSessionId);
  const { currentTheme, data: themeData } = useSelector((state) => state.theme);

  const [activeTab, setActiveTab] = useState("items");

  // ── Guided tour drives tab switching here since `activeTab` is local state ──
  const { active: tourActive, stepIndex: tourStepIndex } = useSelector((state) => state.tour);
  useEffect(() => {
    if (!tourActive) return;
    const step = TOUR_STEPS[tourStepIndex];
    if (step && step.page === "/admin/themeupdate" && step.tabId && step.tabId !== activeTab) {
      setActiveTab(step.tabId);
    }
  }, [tourActive, tourStepIndex, activeTab]);

  // ── Items ──
  const [goodItems,    setGoodItems]    = useState(() => DEFAULT_GOOD_NAMES.map(n => blankItem(n, true)));
  const [badItems,     setBadItems]     = useState(() => DEFAULT_BAD_NAMES.map(n  => blankItem(n, false)));
  const [goodShowItemName, setGoodShowItemName] = useState(false);
  const [badShowItemName,  setBadShowItemName]  = useState(false);
  const [goodShowDesc, setGoodShowDesc] = useState(false);
  const [badShowDesc,  setBadShowDesc]  = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);

  // ── Idle Hearts ──
  const [idleLow,      setIdleLow]      = useState({ file: null, preview: "", filename: "" });
  const [idleMid,      setIdleMid]      = useState({ file: null, preview: "", filename: "" });
  const [idleHigh,     setIdleHigh]     = useState({ file: null, preview: "", filename: "" });
  const [heartsLoading, setHeartsLoading] = useState(false);

  // ── Cloud ──
  const cloudFileRef = useRef(null);
  const [cloud,        setCloud]        = useState({ file: null, preview: "", filename: "" });
  const [cloudLoading, setCloudLoading] = useState(false);

  // ── Feedback ──
  const [feedback,         setFeedback]         = useState(() => DEFAULT_FEEDBACK.map(f => ({ ...f })));
  const [feedbackLoading,  setFeedbackLoading]  = useState(false);
  const [pickerTarget,     setPickerTarget]     = useState(null);
  const [pickerPos,        setPickerPos]        = useState(null);
  const pickerRef       = useRef(null);
  const pickerAnchorRef = useRef(null);

  // ── Leaderboard ──
  const [leaderboard, setLeaderboard] = useState(() => DEFAULT_LEADERBOARD.map(l => ({ ...l })));
  const [lbLoading,   setLbLoading]   = useState(false);

  // ── Bootstrap ──
  useEffect(() => {
    if (currentTheme && adminToken) {
      dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
    }
  }, [dispatch, currentTheme, adminToken]);

  // ── Hydrate from theme data ──
  useEffect(() => {
    if (!themeData) return;

    // Items from JSON
    const rawItems = ensureArr(themeData.heart_items);
    if (rawItems.length > 0) {
      const mapIt = (it) => ({
        name:            String(it.name            ?? ""),
        type:            String(it.type            ?? "text"),
        description:     String(it.description     ?? ""),
        contentFile:     null,
        contentFilename: String(it.contentFilename ?? ""),
        contentPreview:  "",
        mascotType:      String(it.mascotType      ?? "gif"),
        mascotText:      String(it.mascotText      ?? ""),
        mascotFile:      null,
        mascotFilename:  String(it.mascotFilename  ?? ""),
        mascotPreview:   "",
        isGood:          Boolean(it.isGood),
      });
      const goodDB = rawItems.filter(it => it.isGood).map(mapIt);
      const badDB  = rawItems.filter(it => !it.isGood).map(mapIt);
      if (goodDB.length >= MIN_GOOD) setGoodItems(goodDB);
      if (badDB.length  >= MIN_BAD)  setBadItems(badDB);
    }

    setGoodShowItemName(parseStoredBool(themeData.good_show_item_name, false));
    setBadShowItemName(parseStoredBool(themeData.bad_show_item_name, false));
    setGoodShowDesc(parseStoredBool(themeData.good_show_description, false));
    setBadShowDesc(parseStoredBool(themeData.bad_show_description, false));

    // Idle hearts
    if (themeData.idle_heart_low)  setIdleLow(p  => ({ ...p, filename: themeData.idle_heart_low  }));
    if (themeData.idle_heart_mid)  setIdleMid(p  => ({ ...p, filename: themeData.idle_heart_mid  }));
    if (themeData.idle_heart_high) setIdleHigh(p => ({ ...p, filename: themeData.idle_heart_high }));

    // Cloud
    if (themeData.cloud_image) setCloud(p => ({ ...p, filename: themeData.cloud_image }));

    // Feedback
    const fb = ensureArr(themeData.feedback_emojis);
    if (fb.length === DEFAULT_FEEDBACK.length) setFeedback(fb);

    // Leaderboard
    const lb = ensureArr(themeData.leaderboard_tiers);
    if (lb.length === DEFAULT_LEADERBOARD.length) setLeaderboard(lb);
  }, [themeData]);

  // ── Emoji picker close + reposition ──
  const closePicker = useCallback(() => {
    setPickerTarget(null);
    setPickerPos(null);
    pickerAnchorRef.current = null;
  }, []);

  useEffect(() => {
    if (pickerTarget === null) return;
    const onDown = (e) => { if (!pickerRef.current?.contains(e.target)) closePicker(); };
    const onKey  = (e) => { if (e.key === "Escape") closePicker(); };
    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("keydown",   onKey);
    return () => {
      document.removeEventListener("mousedown", onDown, true);
      document.removeEventListener("keydown",   onKey);
    };
  }, [pickerTarget, closePicker]);

  useEffect(() => {
    if (pickerTarget === null) return;
    const reposition = () => { if (pickerAnchorRef.current) setPickerPos(pickPos(pickerAnchorRef.current)); };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [pickerTarget]);

  // ── Item callbacks ──
  const handleGoodUpdate = useCallback((idx, patch) => setGoodItems(p => p.map((it, i) => i === idx ? { ...it, ...patch } : it)), []);
  const handleBadUpdate  = useCallback((idx, patch) => setBadItems( p => p.map((it, i) => i === idx ? { ...it, ...patch } : it)), []);

  const handleGoodRemove = useCallback((idx) => {
    setGoodItems(p => {
      if (p.length <= MIN_GOOD) { Swal.fire("Required", `Minimum ${MIN_GOOD} good items required.`, "warning"); return p; }
      return p.filter((_, i) => i !== idx);
    });
  }, []);

  const handleBadRemove = useCallback((idx) => {
    setBadItems(p => {
      if (p.length <= MIN_BAD) { Swal.fire("Required", `Minimum ${MIN_BAD} bad items required.`, "warning"); return p; }
      return p.filter((_, i) => i !== idx);
    });
  }, []);

  // ── Save items (upload files → save JSON to theme data) ──
  const saveItems = async () => {
    for (const it of [...goodItems, ...badItems]) {
      if (!it.name.trim()) { Swal.fire("Validation", "All items must have a name.", "warning"); return; }
      if (it.name.trim().length > MAX_ITEM_NAME_LEN) {
        Swal.fire("Validation", `Item name "${it.name}" must be ${MAX_ITEM_NAME_LEN} characters or less.`, "warning");
        return;
      }
      if ((it.type === "gif" || it.type === "image") && !it.contentFile && !it.contentFilename) {
        Swal.fire("Validation", `Item "${it.name}" is set to ${it.type.toUpperCase()} but no file is uploaded.`, "warning");
        return;
      }
      if (it.mascotType === "text") {
        if (!String(it.mascotText || "").trim()) {
          Swal.fire("Validation", `Item "${it.name}" is set to mascot TEXT but no text is entered.`, "warning");
          return;
        }
      } else if (!it.mascotFile && !it.mascotFilename) {
        Swal.fire("Validation", `Item "${it.name}" is missing a mascot ${it.mascotType === "gif" ? "GIF" : "image"}.`, "warning");
        return;
      }
    }

    setItemsLoading(true);
    try {
      const processItem = async (item) => {
        let contentFilename = item.contentFilename;
        let mascotFilename  = item.mascotFilename;
        if (item.contentFile) contentFilename = await uploadImageFile(item.contentFile, adminToken, sessionId);
        if (item.mascotType !== "text" && item.mascotFile) mascotFilename = await uploadImageFile(item.mascotFile,  adminToken, sessionId);
        return { ...item, contentFilename, mascotFilename, contentFile: null, mascotFile: null, contentPreview: "", mascotPreview: "" };
      };

      const [updatedGood, updatedBad] = await Promise.all([
        Promise.all(goodItems.map(processItem)),
        Promise.all(badItems.map(processItem)),
      ]);

      const toStore = [
        ...updatedGood.map((it, i) => ({ name: it.name.trim(), type: it.type, description: String(it.description || "").trim().slice(0, MAX_DESC_LEN), contentFilename: it.contentFilename, mascotType: it.mascotType, mascotText: it.mascotType === "text" ? String(it.mascotText || "").trim() : "", mascotFilename: it.mascotType === "text" ? "" : it.mascotFilename, isGood: true,  sortOrder: i + 1 })),
        ...updatedBad.map( (it, i) => ({ name: it.name.trim(), type: it.type, description: String(it.description || "").trim().slice(0, MAX_DESC_LEN), contentFilename: it.contentFilename, mascotType: it.mascotType, mascotText: it.mascotType === "text" ? String(it.mascotText || "").trim() : "", mascotFilename: it.mascotType === "text" ? "" : it.mascotFilename, isGood: false, sortOrder: i + 1 })),
      ];

      await updateThemeData({
        payload: {
          data: {
            heart_items: toStore,
            good_show_item_name: goodShowItemName,
            bad_show_item_name: badShowItemName,
            good_show_description: goodShowDesc,
            bad_show_description: badShowDesc,
          },
          currentTheme,
          organizationId: organizationId || "admin",
          sessionId:      sessionId      || "admin",
        },
        token: adminToken,
      });

      setGoodItems(updatedGood);
      setBadItems(updatedBad);
      await Swal.fire("Saved", "Items saved successfully.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save items", "error");
    } finally {
      setItemsLoading(false);
    }
  };

  // ── Save idle hearts ──
  const saveIdleHearts = async () => {
    const labeled = [
      ["Low Score",  idleLow],
      ["Mid Score",  idleMid],
      ["High Score", idleHigh],
    ];
    for (const [label, state] of labeled) {
      if (!state.file && !state.filename) {
        Swal.fire("Validation", `${label} is missing an idle heart mascot GIF.`, "warning");
        return;
      }
    }

    setHeartsLoading(true);
    try {
      const updated = {};

      const pairs = [
        ["idle_heart_low",  idleLow,  setIdleLow],
        ["idle_heart_mid",  idleMid,  setIdleMid],
        ["idle_heart_high", idleHigh, setIdleHigh],
      ];

      for (const [key, state, setter] of pairs) {
        if (state.file) {
          const filename = await uploadImageFile(state.file, adminToken, sessionId);
          updated[key] = filename;
          setter({ file: null, preview: "", filename });
        } else if (state.filename) {
          updated[key] = state.filename;
        }
      }

      if (Object.keys(updated).length > 0) {
        await updateThemeData({
          payload: {
            data: updated,
            currentTheme,
            organizationId: organizationId || "admin",
            sessionId:      sessionId      || "admin",
          },
          token: adminToken,
        });
      }

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      await Swal.fire("Saved", "Idle heart mascots saved.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save idle hearts", "error");
    } finally {
      setHeartsLoading(false);
    }
  };

  // ── Save cloud ──
  const saveCloud = async () => {
    if (!cloud.file && !cloud.filename) {
      Swal.fire("Required", "Please upload a cloud image first.", "warning");
      return;
    }
    setCloudLoading(true);
    try {
      let filename = cloud.filename;
      if (cloud.file) {
        filename = await uploadImageFile(cloud.file, adminToken, sessionId);
        setCloud({ file: null, preview: "", filename });
      }
      await updateThemeData({
        payload: {
          data: { cloud_image: filename },
          currentTheme,
          organizationId: organizationId || "admin",
          sessionId:      sessionId      || "admin",
        },
        token: adminToken,
      });
      await Swal.fire("Saved", "Cloud image saved.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save cloud", "error");
    } finally {
      setCloudLoading(false);
    }
  };

  // ── Save feedback ──
  const saveFeedback = async () => {
    const trimmed = feedback.map((fb) => ({
      ...fb,
      message: String(fb.message ?? "").trim(),
    }));
    for (const fb of trimmed) {
      if (!fb.emoji) {
        await Swal.fire("Validation", `Set an emoji for "${fb.scoreLabel}".`, "warning");
        return;
      }
      if (!fb.message) {
        await Swal.fire("Validation", `Feedback message for "${fb.scoreLabel}" cannot be empty.`, "warning");
        return;
      }
      if (fb.message.length > MAX_FEEDBACK_MSG_LEN) {
        await Swal.fire(
          "Validation",
          `Feedback message for "${fb.scoreLabel}" must be ${MAX_FEEDBACK_MSG_LEN} characters or less.`,
          "warning"
        );
        return;
      }
    }
    setFeedbackLoading(true);
    try {
      await updateThemeData({
        payload: {
          data: { feedback_emojis: trimmed },
          currentTheme,
          organizationId: organizationId || "admin",
          sessionId:      sessionId      || "admin",
        },
        token: adminToken,
      });
      setFeedback(trimmed);
      await Swal.fire("Saved", "Feedback emojis saved.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save feedback", "error");
    } finally {
      setFeedbackLoading(false);
    }
  };

  // ── Save leaderboard ──
  const saveLeaderboard = async () => {
    const trimmed = leaderboard.map((lb) => ({
      ...lb,
      tierName: String(lb.tierName ?? "").trim(),
    }));
    for (const lb of trimmed) {
      if (!lb.tierName) {
        await Swal.fire("Validation", `Title for "${lb.scoreLabel}" cannot be empty.`, "warning");
        return;
      }
      if (lb.tierName.length > MAX_TIER_NAME_LEN) {
        await Swal.fire(
          "Validation",
          `Title for "${lb.scoreLabel}" must be ${MAX_TIER_NAME_LEN} characters or less.`,
          "warning"
        );
        return;
      }
    }
    setLbLoading(true);
    try {
      await updateThemeData({
        payload: {
          data: { leaderboard_tiers: trimmed },
          currentTheme,
          organizationId: organizationId || "admin",
          sessionId:      sessionId      || "admin",
        },
        token: adminToken,
      });
      setLeaderboard(trimmed);
      await Swal.fire("Saved", "Leaderboard title saved.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save leaderboard", "error");
    } finally {
      setLbLoading(false);
    }
  };

  // ── Cloud file handler ──
  async function handleCloudFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".png")) {
      Swal.fire("Invalid File", "Cloud image must be a PNG file.", "warning");
      e.target.value = "";
      return;
    }
    if (!isFileSizeOk(file)) {
      e.target.value = "";
      return;
    }
    setCloud({ file, preview: URL.createObjectURL(file), filename: file.name });
  }

  // ────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────
  return (
    <div className="themeupdate-page">
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
      </div>

      {/* ── Tab Navigation ── */}
      <div id="tour-tu-tabs" className="hs-tabs-bar">
        {[
          { id: "items",       icon: "fa-heart",       label: "Items"           },
          { id: "idle",        icon: "fa-bed-pulse",   label: "Idle Hearts"     },
          { id: "clouds",      icon: "fa-cloud",       label: "Clouds"          },
          { id: "feedback",    icon: "fa-face-smile",  label: "Feedback Emojis" },
          { id: "leaderboard", icon: "fa-trophy",      label: "Leaderboard"     },
        ].map(tab => (
          <button
            key={tab.id}
            type="button"
            className={`hs-tab-btn${activeTab === tab.id ? " hs-tab-btn--active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <i className={`fa-solid ${tab.icon}`} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ═══════════════ ITEMS TAB ═══════════════ */}
      {activeTab === "items" && (
        <div className="hs-tab-content">
          {itemsLoading ? (
            <div className="themeupdate-loading">Saving items…</div>
          ) : (
            <>
              {/* Good Items */}
              <div className="hs-section">
                <div className="hs-section-header hs-section-header--good">
                  <span><i className="fa-solid fa-heart" /> Good for Heart Health</span>
                  <span className="hs-section-count">{goodItems.length} / {MAX_GOOD}</span>
                </div>
                <div className="hs-toggle-row">
                  <ItemNameToggle
                    checked={goodShowItemName}
                    onChange={setGoodShowItemName}
                    disabled={itemsLoading}
                  />
                  <ItemNameToggle
                    label="Description"
                    checked={goodShowDesc}
                    onChange={setGoodShowDesc}
                    disabled={itemsLoading}
                  />
                </div>
                <div id="tour-tu-items-grid" className="hs-items-grid">
                  {goodItems.map((item, idx) => (
                    <ItemCard
                      key={idx}
                      item={item}
                      idx={idx}
                      isGood={true}
                      showDescription={goodShowDesc}
                      onUpdate={handleGoodUpdate}
                      onRemove={handleGoodRemove}
                      disabled={itemsLoading}
                    />
                  ))}
                  {goodItems.length < MAX_GOOD && (
                    <button
                      type="button"
                      className="hs-add-item-btn hs-add-item-btn--good"
                      onClick={() => {
                        if (goodItems.length >= MAX_GOOD) return;
                        setGoodItems(p => [...p, blankItem("", true)]);
                      }}
                    >
                      <i className="fa-solid fa-plus" />
                      <span>Add Good Item</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Bad Items */}
              <div className="hs-section">
                <div className="hs-section-header hs-section-header--bad">
                  <span><i className="fa-solid fa-heart-crack" /> Bad for Heart Health</span>
                  <span className="hs-section-count">{badItems.length} / {MAX_BAD}</span>
                </div>
                <div className="hs-toggle-row">
                  <ItemNameToggle
                    checked={badShowItemName}
                    onChange={setBadShowItemName}
                    disabled={itemsLoading}
                  />
                  <ItemNameToggle
                    label="Description"
                    checked={badShowDesc}
                    onChange={setBadShowDesc}
                    disabled={itemsLoading}
                  />
                </div>
                <div className="hs-items-grid">
                  {badItems.map((item, idx) => (
                    <ItemCard
                      key={idx}
                      item={item}
                      idx={idx}
                      isGood={false}
                      showDescription={badShowDesc}
                      onUpdate={handleBadUpdate}
                      onRemove={handleBadRemove}
                      disabled={itemsLoading}
                    />
                  ))}
                  {badItems.length < MAX_BAD && (
                    <button
                      type="button"
                      className="hs-add-item-btn hs-add-item-btn--bad"
                      onClick={() => {
                        if (badItems.length >= MAX_BAD) return;
                        setBadItems(p => [...p, blankItem("", false)]);
                      }}
                    >
                      <i className="fa-solid fa-plus" />
                      <span>Add Bad Item</span>
                    </button>
                  )}
                </div>
              </div>

              <div id="tour-tu-items-save" className="hs-actions-bar">
                <p className="themeupdate-muted">
                  <code>
                    Good: min {MIN_GOOD}, max {MAX_GOOD} &nbsp;·&nbsp;
                    Bad: min {MIN_BAD}, max {MAX_BAD} &nbsp;·&nbsp;
                    Mascots: text / PNG / GIF, any size, max 2 MB
                  </code>
                </p>
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-success"
                  onClick={saveItems}
                  disabled={itemsLoading}
                >
                  <i className="fa-solid fa-floppy-disk" /> Save Items
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ═══════════════ IDLE HEARTS TAB ═══════════════ */}
      {activeTab === "idle" && (
        <div className="hs-tab-content">
          <div id="tour-tu-idle-card" className="hs-card">
            <div className="hs-card-head">
              <i className="fa-solid fa-bed-pulse" /> Idle Heart Mascots
            </div>
            <div className="hs-card-body">
              <p className="themeupdate-muted">
                <code>Upload GIF mascots (any size, max 2 MB) for each score state. Shown when the heart is idle.</code>
              </p>
              <div className="hs-idle-grid">
                <IdleCard label="Low Score"  description="Score ≤ 20"    state={idleLow}  onFile={setIdleLow}  />
                <IdleCard label="Mid Score"  description="Score 25 – 60" state={idleMid}  onFile={setIdleMid}  />
                <IdleCard label="High Score" description="Score 65+"     state={idleHigh} onFile={setIdleHigh} />
              </div>
              <div className="hs-actions-bar">
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-success"
                  onClick={saveIdleHearts}
                  disabled={heartsLoading}
                >
                  {heartsLoading
                    ? <><i className="fa-solid fa-spinner fa-spin" /> Uploading…</>
                    : <><i className="fa-solid fa-floppy-disk" /> Save Idle Hearts</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════ CLOUDS TAB ═══════════════ */}
      {activeTab === "clouds" && (
        <div className="hs-tab-content">
          <div id="tour-tu-cloud-card" className="hs-card">
            <div className="hs-card-head">
              <i className="fa-solid fa-cloud" /> Cloud Image
            </div>
            <div className="hs-card-body">
              <p className="themeupdate-muted">
                <code>Upload the cloud image used as a game backdrop element (PNG only, any size, max 2 MB; fitted automatically in the game).</code>
              </p>
              <div className="hs-cloud-area">
                <div
                  className="hs-upload-area hs-upload-area--cloud"
                  onClick={() => cloudFileRef.current?.click()}
                >
                  {cloud.preview || cloud.filename ? (
                    <img
                      src={cloud.preview || getDvSourceImageUrl(cloud.filename)}
                      alt="cloud"
                      className="hs-upload-thumb-lg"
                    />
                  ) : (
                    <div className="hs-upload-empty">
                      <i className="fa-solid fa-cloud" />
                      <span>Upload Cloud Image</span>
                    </div>
                  )}
                </div>
                <input
                  ref={cloudFileRef}
                  type="file"
                  accept=".png"
                  className="hs-file-hidden"
                  onChange={handleCloudFile}
                />
                <span className="hs-upload-hint">.png only, any size, max 2 MB</span>
              </div>
              <div className="hs-actions-bar">
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-success"
                  onClick={saveCloud}
                  disabled={cloudLoading}
                >
                  {cloudLoading
                    ? <><i className="fa-solid fa-spinner fa-spin" /> Uploading…</>
                    : <><i className="fa-solid fa-floppy-disk" /> Save Cloud</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════ FEEDBACK TAB ═══════════════ */}
      {activeTab === "feedback" && (
        <div className="hs-tab-content">
          <div id="tour-tu-feedback-card" className="hs-card">
            <div className="hs-card-head">
              <i className="fa-solid fa-face-smile" /> In-Game Feedback Emojis
            </div>
            <div className="hs-card-body">
              <p className="themeupdate-muted">
                <code>
                  Edit the emoji and message shown to players based on their score.
                  Message required, max {MAX_FEEDBACK_MSG_LEN} characters each.
                </code>
              </p>
              <div className="hs-feedback-list">
                {feedback.map((fb, i) => (
                  <div key={fb.scoreKey} className="hs-feedback-row">
                    <span className="hs-score-badge">{fb.scoreLabel}</span>
                    <button
                      type="button"
                      className="hs-emoji-btn"
                      title="Click to change emoji"
                      onClick={e => {
                        pickerAnchorRef.current = e.currentTarget;
                        setPickerPos(pickPos(e.currentTarget));
                        setPickerTarget(i);
                      }}
                    >
                      {fb.unified ? (
                        <Emoji unified={fb.unified} emojiStyle={EmojiStyle.APPLE} size={30} />
                      ) : (
                        <span className="hs-emoji-fallback">{fb.emoji || "?"}</span>
                      )}
                    </button>
                    <div className="hs-feedback-input-wrap">
                      <input
                        type="text"
                        className="themeupdate-input hs-feedback-msg"
                        value={fb.message}
                        maxLength={MAX_FEEDBACK_MSG_LEN}
                        placeholder="Feedback message…"
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v.length > MAX_FEEDBACK_MSG_LEN) {
                            Swal.fire(
                              "Limit",
                              `Feedback message max ${MAX_FEEDBACK_MSG_LEN} characters.`,
                              "warning"
                            );
                            return;
                          }
                          setFeedback((p) =>
                            p.map((x, j) => (j === i ? { ...x, message: v } : x))
                          );
                        }}
                      />
                      <span className="hs-char-count">
                        {String(fb.message || "").length}/{MAX_FEEDBACK_MSG_LEN}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="hs-actions-bar">
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-success"
                  onClick={saveFeedback}
                  disabled={feedbackLoading}
                >
                  <i className="fa-solid fa-floppy-disk" /> Save Feedback
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════ LEADERBOARD TAB ═══════════════ */}
      {activeTab === "leaderboard" && (
        <div className="hs-tab-content">
          <div id="tour-tu-leaderboard-card" className="hs-card">
            <div className="hs-card-head">
              <i className="fa-solid fa-trophy" /> Leaderboard Title
            </div>
            <div className="hs-card-body">
              <p className="themeupdate-muted">
                <code>
                  Edit the title shown on the leaderboard next to each player's score.
                  Required, max {MAX_TIER_NAME_LEN} characters each.
                </code>
              </p>
              <div className="hs-lb-list">
                {leaderboard.map((lb, i) => (
                  <div key={lb.scoreKey} className="hs-lb-row">
                    <span className="hs-score-badge hs-score-badge--lb">{lb.scoreLabel}</span>
                    <div className="hs-lb-trophy"><i className="fa-solid fa-trophy" /></div>
                    <div className="hs-lb-input-wrap">
                      <input
                        type="text"
                        className="themeupdate-input hs-lb-input"
                        value={lb.tierName}
                        maxLength={MAX_TIER_NAME_LEN}
                        placeholder="Title…"
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v.length > MAX_TIER_NAME_LEN) {
                            Swal.fire(
                              "Limit",
                              `Title max ${MAX_TIER_NAME_LEN} characters.`,
                              "warning"
                            );
                            return;
                          }
                          setLeaderboard((p) =>
                            p.map((x, j) => (j === i ? { ...x, tierName: v } : x))
                          );
                        }}
                      />
                      <span className="hs-char-count">
                        {String(lb.tierName || "").length}/{MAX_TIER_NAME_LEN}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="hs-actions-bar">
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-success"
                  onClick={saveLeaderboard}
                  disabled={lbLoading}
                >
                  <i className="fa-solid fa-floppy-disk" /> Save Leaderboard
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Emoji Picker Portal */}
      {pickerTarget !== null && pickerPos
        ? createPortal(
            <div
              ref={pickerRef}
              className="themeupdate-emoji-picker-portal"
              style={{ top: pickerPos.top, left: pickerPos.left }}
            >
              <EmojiPicker
                onEmojiClick={emojiData => {
                  const unified = String(emojiData?.unified ?? "").toLowerCase();
                  const emoji   = String(emojiData?.emoji   ?? "");
                  setFeedback(p => p.map((x, i) => i === pickerTarget ? { ...x, emoji, unified } : x));
                  closePicker();
                }}
                theme={Theme.LIGHT}
                emojiStyle={EmojiStyle.APPLE}
                width={EMOJI_W}
                height={EMOJI_H}
                lazyLoadEmojis={false}
                previewConfig={{ showPreview: true, defaultCaption: "Pick an emoji" }}
              />
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default ThemeUpdate;
